import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import {
  checkForErrors,
  getPypeConnections,
  toSolutionEnvironment,
  type PlatformConnection,
} from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

/**
 * (Re)binds a channel's pype (per env) to a Live Agent Platform connection.
 * `addPypeToPypeConnection` is an idempotent upsert that does NOT detach the
 * pype from other connections, so a single-select switch removes it from the
 * previous connection first, then adds it to the new one. Either side may be
 * absent (initial bind / unbind) — mirrors `assignPypeRuleGroup`.
 *
 * The backend writes `agent_routing` / `agent_routing_config` /
 * `agent_routing_url` onto the pype for both calls; the channel form no longer
 * copies the connection's config onto itself.
 */
export const assignPypeConnection = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{
  pypeConnections: PlatformConnection[];
  cb?: () => void;
} | void> => {
  if (event.type !== 'assignPypeConnection') {
    return;
  }

  const projectId = ctx.project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  const { pypeId, env, fromConnectionId, toConnectionId, cb } = event.payload;
  const solutionEnv = toSolutionEnvironment(env);

  if (fromConnectionId) {
    const removeResponse = await urqlGql.removePypeFromPypeConnection({
      id: fromConnectionId,
      pypeId,
      env: solutionEnv,
    });
    checkForErrors(removeResponse);
  }

  if (toConnectionId) {
    const addResponse = await urqlGql.addPypeToPypeConnection({
      id: toConnectionId,
      pypeId,
      env: solutionEnv,
    });
    checkForErrors(addResponse);
  }

  const pypeConnections = await getPypeConnections({ projectId });

  return { pypeConnections, cb };
};
